// LandingPage.jsx - Enhanced with Dynamic Live Streams from Supabase + Discord CTA
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SignInButton, SignUpButton, SignedIn, SignedOut, UserButton, SignOutButton, ClerkLoaded, ClerkLoading } from '@clerk/clerk-react';
import { supabase } from './lib/supabase';
import './LandingPage.css';
import CreateGameModal from './CreateGameModal';
import PageBG from './components/PageBG';

export default function LandingPage() {
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handlePlayAsGuest = () => {
    setIsCreateModalOpen(true);
  };

  // Load streams from Supabase
  const loadStreams = async () => {
    try {
      const { data, error } = await supabase
        .from('streams')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        // Organize streams by type and map database fields to component fields
        const organized = {
          upcoming: data
            .filter(s => s.stream_type === 'upcoming')
            .map(s => ({
              id: s.id,
              title: s.title,
              date: s.date_time,
              thumbnail: s.thumbnail,
              youtubeUrl: s.youtube_url,
              status: s.status,
              description: s.description
            })),
          recent: data
            .filter(s => s.stream_type === 'recent')
            .map(s => ({
              id: s.id,
              title: s.title,
              date: s.date_time,
              thumbnail: s.thumbnail,
              youtubeUrl: s.youtube_url,
              viewCount: s.view_count,
              highlights: s.highlights
            }))
        };
        
        console.log('Loaded streams from Supabase:', organized);
        return organized;
      }
    } catch (error) {
      console.error('Error loading streams from Supabase:', error);
    }
    
    // Return defaults if Supabase fails or no data
    console.log('Using default streams');
    return {
      upcoming: [
        {
          id: 1,
          title: "🔴 LIVE: Frozzlers Friday Night Fights - Tournament #5",
          date: "Friday, Sept 27 @ 8PM EST",
          thumbnail: "/streams/friday-fights.jpg",
          youtubeUrl: "https://youtube.com/live/YOUR_STREAM_ID_1",
          status: "upcoming",
          description: "Weekly tournament - Join with code: FRIDAY5"
        }
      ],
      recent: [
        {
          id: 4,
          title: "Epic AI Tournament - Insane vs PhD Scientist",
          date: "Sept 20, 2025",
          thumbnail: "/streams/ai-tournament.jpg",
          youtubeUrl: "https://youtube.com/watch?v=YOUR_VIDEO_ID_1",
          viewCount: "1.2K views",
          highlights: "3 perfect games by Insane AI!"
        }
      ]
    };
  };

  const [streams, setStreams] = useState({ upcoming: [], recent: [] });
  const [streamsLoading, setStreamsLoading] = useState(true);

  // Load streams on component mount
  useEffect(() => {
    const fetchStreams = async () => {
      setStreamsLoading(true);
      const data = await loadStreams();
      setStreams(data);
      setStreamsLoading(false);
    };
    
    fetchStreams();

    // Set up real-time subscription for changes
    const subscription = supabase
      .channel('streams-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'streams' }, 
        () => {
          console.log('Streams updated, reloading...');
          fetchStreams();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Render thumbnail with fallback
  const renderThumbnail = (stream, isUpcoming = true) => {
    if (stream.thumbnail && stream.thumbnail.startsWith('data:')) {
      // Base64 encoded image from upload
      return <img src={stream.thumbnail} alt={stream.title} style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      }} />;
    } else if (stream.thumbnail && stream.thumbnail.startsWith('http')) {
      // External URL
      return <img src={stream.thumbnail} alt={stream.title} style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      }} onError={(e) => {
        e.target.style.display = 'none';
      }} />;
    } else {
      // Fallback icon
      return (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '3rem'
        }}>
          {isUpcoming ? '🎮' : '🎬'}
        </div>
      );
    }
  };

  return (
    <PageBG src="/bg/theyre_here.jpg">
      <div className="landing-page-wrapper">
        {/* Render the Create Game modal when its state is true */}
        {isCreateModalOpen && <CreateGameModal onClose={() => setIsCreateModalOpen(false)} isGuestMode={true} />}
        
        <div className="bg-animation">
          <div className="floating-puck puck-1"></div>
          <div className="floating-puck puck-2"></div>
          <div className="floating-puck puck-3"></div>
          <div className="floating-puck puck-4"></div>
        </div>
        
        <div className="container">
          <nav>
            <div className="logo">
              <div className="logo-icon">F</div>
              FROZZLERS
            </div>
            <div className="nav-buttons">
              <ClerkLoading>
                <div className="nav-loading" style={{color:"#94a3b8", fontSize:"14px"}}>Signing you in…</div>
              </ClerkLoading>
              <ClerkLoaded>
                <SignedOut>
                  <SignInButton mode="modal">
                    <button className="btn-nav btn-signin">Sign In</button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <button className="btn-nav btn-signup">Sign Up</button>
                  </SignUpButton>
                </SignedOut>

                <SignedIn>
                  <Link 
                    to="/dashboard" 
                    className="btn-nav btn-dashboard" 
                    style={{ minWidth: '130px', textAlign: 'center' }}
                  >
                    Dashboard
                  </Link>

                  <Link 
                    to="/dashboard?action=create"
                    className="btn-nav btn-play" 
                    style={{ minWidth: '130px', textAlign: 'center' }}
                  >
                    Play Online Game
                  </Link>
      
                  <SignOutButton>
                    <button 
                      className="btn-nav btn-signout" 
                      style={{ minWidth: '130px', textAlign: 'center' }}
                    >
                      Sign Out
                    </button>
                  </SignOutButton>

                  <UserButton afterSignOutUrl="/" />
                </SignedIn>
              </ClerkLoaded>
            </div>
          </nav>

          <div className="hero">
            <div className="hero-logo">
              <svg viewBox="0 0 400 120" xmlns="http://www.w3.org/2000/svg" style={{width: '100%', height: 'auto'}}>
                <defs>
                  <linearGradient id="iceGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor:"#60a5fa",stopOpacity:1}} />
                    <stop offset="50%" style={{stopColor:"#3b82f6",stopOpacity:1}} />
                    <stop offset="100%" style={{stopColor:"#1e40af",stopOpacity:1}} />
                  </linearGradient>
                  <linearGradient id="alienGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor:"#a855f7",stopOpacity:1}} />
                    <stop offset="50%" style={{stopColor:"#7c3aed",stopOpacity:1}} />
                    <stop offset="100%" style={{stopColor:"#5b21b6",stopOpacity:1}} />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <circle cx="60" cy="60" r="45" fill="url(#iceGradient)" opacity="0.3" filter="url(#glow)"/>
                <circle cx="60" cy="60" r="45" fill="none" stroke="url(#iceGradient)" strokeWidth="2"/>
                <circle cx="60" cy="60" r="35" fill="none" stroke="#34d399" strokeWidth="1.5" opacity="0.8"/>
                <circle cx="60" cy="60" r="25" fill="none" stroke="#fbbf24" strokeWidth="1.5" opacity="0.8"/>
                <circle cx="60" cy="60" r="15" fill="none" stroke="#f87171" strokeWidth="1.5" opacity="0.8"/>
                <circle cx="60" cy="60" r="3" fill="white" filter="url(#glow)"/>
                <circle cx="45" cy="45" r="4" fill="#ef4444"/>
                <circle cx="75" cy="75" r="4" fill="#3b82f6"/>
                <circle cx="75" cy="45" r="4" fill="#10b981"/>
                <text x="140" y="45" fontFamily="Arial Black, sans-serif" fontSize="28" fontWeight="900" fill="url(#iceGradient)" filter="url(#glow)">FROZZLERS</text>
                <text x="140" y="65" fontFamily="Arial, sans-serif" fontSize="12" fill="url(#alienGradient)" opacity="0.9">ICE • ALIENS • STRATEGY</text>
                <text x="140" y="85" fontFamily="Arial, sans-serif" fontSize="10" fill="#94a3b8">Multiplayer Curling Combat</text>
              </svg>
            </div>
            <h1>Strategic Multiplayer<br />Curling Combat</h1>
            <p className="subtitle">Ice meets aliens in this tactical puck-sliding showdown</p>
            <p>Master the art of precision and strategy in this unique blend of curling and tactical combat. Challenge friends, climb leaderboards, and prove your skills on the ice!</p>
            
            <button onClick={handlePlayAsGuest} className="cta-button">Play as Guest →</button>
            <p style={{color: '#94a3b8', fontSize: '16px', lineHeight: '1.6', maxWidth: '600px', margin: '1rem auto', padding: '0 1rem'}}>
              Playing as guest allows you to fully experience the game by competing against AI Players or against your friends in a hot seat setup. For full online multiplayer, tracking your stats, leaderboards and tournaments - please make an account. We hope you have a good time here!
            </p>
          </div>

          {/* ========== DISCORD COMMUNITY CTA ========== */}
          <div className="discord-cta-section" style={{
            margin: '4rem auto',
            maxWidth: '1000px',
            padding: '0 1rem'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #5865F2 0%, #7289DA 100%)',
              borderRadius: '16px',
              padding: '3rem 2rem',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(88, 101, 242, 0.3)',
              border: '2px solid rgba(255, 255, 255, 0.1)'
            }}>
              {/* Decorative background elements */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '200px',
                height: '200px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                filter: 'blur(40px)'
              }} />
              <div style={{
                position: 'absolute',
                bottom: '-50px',
                left: '-50px',
                width: '200px',
                height: '200px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                filter: 'blur(40px)'
              }} />
              
              {/* Content */}
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  fontSize: '4rem',
                  marginBottom: '1rem',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                }}>
                  💬
                </div>
                
                <h2 style={{
                  fontSize: '2.5rem',
                  color: '#ffffff',
                  marginBottom: '1rem',
                  fontWeight: '800',
                  textShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  Join Our Discord Community!
                </h2>
                
                <p style={{
                  fontSize: '1.2rem',
                  color: '#ffffff',
                  marginBottom: '2rem',
                  maxWidth: '700px',
                  margin: '0 auto 2rem auto',
                  opacity: 0.95,
                  lineHeight: '1.6'
                }}>
                  Connect with other players, find teammates, participate in tournaments, get game updates, and chat with the developer. Join our growing community of Frozzlers players!
                </p>
                
                <a
                  href="https://discord.gg/uQhBmxM3"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '18px 36px',
                    background: '#ffffff',
                    color: '#5865F2',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
                  }}
                  className="discord-join-btn"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.3)';
                    e.currentTarget.style.background = '#f0f0f0';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)';
                    e.currentTarget.style.background = '#ffffff';
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 71 55" fill="currentColor">
                    <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                  </svg>
                  Join Discord Server
                </a>
                
                <div style={{
                  marginTop: '1.5rem',
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '2rem',
                  flexWrap: 'wrap',
                  fontSize: '0.95rem',
                  color: '#ffffff',
                  opacity: 0.9
                }}>
                  <span>✨ Exclusive Updates</span>
                  <span>🏆 Find Tournament Partners</span>
                  <span>💡 Share Strategies</span>
                </div>
              </div>
            </div>
          </div>

          {/* LIVE STREAMS SECTION - Now from Supabase! */}
          {!streamsLoading && (streams.upcoming.length > 0 || streams.recent.length > 0) && (
            <div className="streams-section" style={{
              margin: '4rem auto',
              maxWidth: '1200px',
              padding: '0 1rem'
            }}>
              <h2 style={{
                fontSize: '2.5rem',
                textAlign: 'center',
                marginBottom: '1rem',
                background: 'linear-gradient(135deg, #60a5fa, #a855f7)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                🎮 Live Streams & Community Play
              </h2>
              <p style={{
                textAlign: 'center',
                color: '#94a3b8',
                fontSize: '1.1rem',
                marginBottom: '3rem'
              }}>
                Join our live development sessions and multiplayer tournaments on YouTube!
              </p>

              {/* Upcoming Streams */}
              {streams.upcoming.length > 0 && (
                <div className="upcoming-streams" style={{ marginBottom: '3rem' }}>
                  <h3 style={{
                    fontSize: '1.8rem',
                    marginBottom: '1.5rem',
                    color: '#e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ color: '#ef4444' }}>🔴</span> Upcoming Streams
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                    gap: '1.5rem'
                  }}>
                    {streams.upcoming.map(stream => (
                      <a 
                        key={stream.id}
                        href={stream.youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: 'none',
                          display: 'block',
                          transition: 'transform 0.3s ease'
                        }}
                        className="stream-card"
                      >
                        <div style={{
                          background: 'rgba(30, 41, 59, 0.8)',
                          border: '1px solid rgba(96, 165, 250, 0.3)',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}>
                          {/* Thumbnail */}
                          <div style={{
                            position: 'relative',
                            paddingBottom: '56.25%',
                            background: `linear-gradient(135deg, #1e40af, #5b21b6)`,
                            overflow: 'hidden'
                          }}>
                            {renderThumbnail(stream, true)}
                            {/* LIVE Badge */}
                            <div style={{
                              position: 'absolute',
                              top: '10px',
                              left: '10px',
                              background: '#ef4444',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.8rem',
                              fontWeight: 'bold'
                            }}>
                              UPCOMING
                            </div>
                          </div>
                          {/* Stream Info */}
                          <div style={{ padding: '1rem' }}>
                            <h4 style={{
                              color: '#e5e7eb',
                              fontSize: '1.1rem',
                              marginBottom: '0.5rem',
                              lineHeight: '1.4'
                            }}>
                              {stream.title}
                            </h4>
                            <p style={{
                              color: '#60a5fa',
                              fontSize: '0.9rem',
                              marginBottom: '0.5rem',
                              fontWeight: '600'
                            }}>
                              📅 {stream.date}
                            </p>
                            <p style={{
                              color: '#94a3b8',
                              fontSize: '0.9rem',
                              lineHeight: '1.4'
                            }}>
                              {stream.description}
                            </p>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Streams */}
              {streams.recent.length > 0 && (
                <div className="recent-streams">
                  <h3 style={{
                    fontSize: '1.8rem',
                    marginBottom: '1.5rem',
                    color: '#e5e7eb'
                  }}>
                    📹 Recent Streams & Highlights
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                    gap: '1.5rem'
                  }}>
                    {streams.recent.map(stream => (
                      <a 
                        key={stream.id}
                        href={stream.youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: 'none',
                          display: 'block'
                        }}
                        className="stream-card"
                      >
                        <div style={{
                          background: 'rgba(30, 41, 59, 0.6)',
                          border: '1px solid rgba(148, 163, 184, 0.2)',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}>
                          {/* Thumbnail */}
                          <div style={{
                            position: 'relative',
                            paddingBottom: '56.25%',
                            background: `linear-gradient(135deg, #334155, #1e293b)`,
                            overflow: 'hidden'
                          }}>
                            {renderThumbnail(stream, false)}
                            {/* View count badge */}
                            {stream.viewCount && (
                              <div style={{
                                position: 'absolute',
                                bottom: '10px',
                                right: '10px',
                                background: 'rgba(0, 0, 0, 0.8)',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '0.8rem'
                              }}>
                                {stream.viewCount}
                              </div>
                            )}
                          </div>
                          {/* Stream Info */}
                          <div style={{ padding: '1rem' }}>
                            <h4 style={{
                              color: '#e5e7eb',
                              fontSize: '1rem',
                              marginBottom: '0.5rem',
                              lineHeight: '1.4'
                            }}>
                              {stream.title}
                            </h4>
                            <p style={{
                              color: '#94a3b8',
                              fontSize: '0.85rem',
                              marginBottom: '0.3rem'
                            }}>
                              {stream.date}
                            </p>
                            {stream.highlights && (
                              <p style={{
                                color: '#60a5fa',
                                fontSize: '0.85rem',
                                fontStyle: 'italic'
                              }}>
                                ✨ {stream.highlights}
                              </p>
                            )}
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Subscribe CTA */}
              <div style={{
                textAlign: 'center',
                marginTop: '3rem',
                padding: '2rem',
                background: 'rgba(96, 165, 250, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(96, 165, 250, 0.3)'
              }}>
                <h3 style={{ color: '#60a5fa', marginBottom: '1rem' }}>
                  🔔 Never Miss a Stream!
                </h3>
                <p style={{ color: '#e5e7eb', marginBottom: '1.5rem' }}>
                  Subscribe to our YouTube channel for notifications when we go live
                </p>
                <a 
                  href="https://www.youtube.com/@aiworkflowmanager42"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '12px 24px',
                    background: '#ef4444',
                    color: 'white',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    transition: 'background 0.3s ease'
                  }}
                  className="youtube-subscribe-btn"
                >
                  Subscribe on YouTube →
                </a>
              </div>
            </div>
          )}

          <div className="features">
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>Precision Gameplay</h3>
              <p>Master the perfect balance of power and aim in this skill-based curling experience.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">👥</div>
              <h3>Multiplayer Action</h3>
              <p>Challenge up to 4 players in intense local and online matches with rotating sectors.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🏆</div>
              <h3>Competitive Rankings</h3>
              <p>Track your progress, climb global leaderboards, and unlock achievements.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎮</div>
              <h3>Easy to Learn</h3>
              <p>Simple controls with deep strategy. Place, aim, and release for tactical gameplay.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🌟</div>
              <h3>Dynamic Arenas</h3>
              <p>Randomized rock obstacles and rotating launch sectors keep every match fresh.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Real-time Physics</h3>
              <p>Realistic puck collisions, friction, and momentum for authentic curling action.</p>
            </div>
          </div>

          <div className="game-preview">
            <h2>Experience the Action</h2>
            <div className="preview-video">
              <video 
                width="800" 
                height="450" 
                controls 
                autoPlay 
                muted 
                loop
                poster="/frozzlers_thumbnail.jpg"
                style={{
                  width: '100%',
                  maxWidth: '800px',
                  height: 'auto',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  border: '2px solid rgba(96, 165, 250, 0.2)'
                }}
              >
                <source src="/frozzlers_gameplay.mp4" type="video/mp4" />
                <source src="/frozzlers_gameplay.webm" type="video/webm" />
                Your browser does not support the video tag.
              </video>
            </div>
            <p style={{color: '#e5e7eb', fontSize: '1.1rem', marginBottom: '2rem'}}>
              Watch skilled players battle it out in real-time matches with strategic puck placement and tactical scoring.
            </p>
          </div>

          <div className="footer">
            <p>Ready to dominate the ice?</p>
            <SignUpButton mode="modal">
              <button className="cta-button" style={{display: 'inline-block', margin: '1rem 0'}}>Create Your Account →</button>
            </SignUpButton>
            <p style={{marginTop: '2rem'}}>Created with ❄️ and 🛸 | <Link to="/game">Play Now</Link> | <Link to="/leaderboards">Leaderboards</Link></p>
          </div>
        </div>
      </div>
    </PageBG>
  );
}
